import { THROUGHLINE_STATE_DEFINITIONS, type ThroughlineGapAlert } from "@/lib/throughlineAdapter";
import type { ThroughlineDayRollup } from "@/hooks/useThroughlinePresentation";

export type CoachThroughlineSelectedDayViewModel = {
  heading: string;
  state: ThroughlineDayRollup["state"];
  stateLabel: string;
  confidenceLabel: string;
  summary: string;
  confidenceReason: string;
  saidSignals: string[];
  unsaidSignals: string[];
  coachAttention: string | null;
};

function buildConfidenceLabel(confidence: ThroughlineDayRollup["confidence"]) {
  if (confidence === "high") return "High confidence";
  if (confidence === "medium") return "Medium confidence";
  return "Low confidence";
}

function buildCoachAttention(throughlineGapAlert: ThroughlineGapAlert) {
  if (!throughlineGapAlert.active) return null;
  return `Recent pattern: ${throughlineGapAlert.gapDays} alignment-gap days and ${throughlineGapAlert.lowEvidenceDays} low-evidence days in the last ${throughlineGapAlert.recentWindowSize} days.`;
}

function buildSaidSignals(selectedThroughlineRollup: ThroughlineDayRollup) {
  const explicitSignals = [
    ...selectedThroughlineRollup.itemsByType.transcript.map((item) => item.detail),
    ...selectedThroughlineRollup.itemsByType.reflection.map((item) => item.detail),
  ]
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 3);

  if (explicitSignals.length > 0) {
    return explicitSignals;
  }

  return (selectedThroughlineRollup.drivers ?? [])
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 3);
}

export function buildCoachThroughlineSelectedDay(params: {
  selectedThroughlineRollup: ThroughlineDayRollup | null;
  throughlineUnsaidSignals: string[];
  throughlineGapAlert: ThroughlineGapAlert;
}): CoachThroughlineSelectedDayViewModel | null {
  const { selectedThroughlineRollup, throughlineUnsaidSignals, throughlineGapAlert } = params;
  if (!selectedThroughlineRollup) return null;

  const saidSignals = buildSaidSignals(selectedThroughlineRollup);
  const unsaidSignals = throughlineUnsaidSignals
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 3);

  return {
    heading: selectedThroughlineRollup.label,
    state: selectedThroughlineRollup.state,
    stateLabel: THROUGHLINE_STATE_DEFINITIONS[selectedThroughlineRollup.state].label,
    confidenceLabel: buildConfidenceLabel(selectedThroughlineRollup.confidence),
    summary: selectedThroughlineRollup.summary,
    confidenceReason: selectedThroughlineRollup.confidenceReason,
    saidSignals,
    unsaidSignals: unsaidSignals.length > 0
      ? unsaidSignals
      : ["No major unsaid gap detected for this day based on current evidence."],
    coachAttention: buildCoachAttention(throughlineGapAlert),
  };
}
